import React from "react";
import { Box, Checkbox, Link, Typography } from "@mui/material";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import { ConfirmModal } from "global/components/AppModal";
import strings from "global/constants/StringConstants";
import urls from "global/constants/UrlConstants";
import { TickGlyph } from "./RecipientsIcons";
import recipientsTokens from "../ApproversAndSigners.tokens";

const { accent } = recipientsTokens;

/**
 * Shown on Next from the Approvers & Signers step when any signer is set to
 * receive the request by text (SMS). The sender must confirm the recipient has
 * agreed to be texted before the flow can continue.
 */
const SmsConsentDialog = (props: {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  /** Recording consent server-side — disable inputs and spin the button. */
  confirming?: boolean;
}) => {
  const { open, onCancel, onConfirm, confirming = false } = props;
  const [checked, setChecked] = React.useState(false);

  // Reset the confirmation each time the dialog opens.
  React.useEffect(() => {
    if (open) setChecked(false);
  }, [open]);

  const infoRow = (Icon: typeof SmsOutlinedIcon, label: string, text: string) => (
    <Box sx={styles.infoRow}>
      <Box sx={styles.infoIconTile}>
        <Icon sx={styles.infoIcon} />
      </Box>
      <Typography sx={styles.infoText}>
        <Box component="span" sx={styles.infoLabel}>
          {label}
        </Box>
        {text}
      </Typography>
    </Box>
  );

  return (
    <ConfirmModal
      open={open}
      onClose={onCancel}
      title={strings.smsDelivery.consentTitle}
      subtitle={strings.smsDelivery.consentSubtitle}
      icon={<SmsOutlinedIcon sx={{ fontSize: 19 }} />}
      size="md"
      confirmLabel={strings.smsDelivery.consentContinue}
      cancelLabel={strings.cancel}
      onConfirm={onConfirm}
      loading={confirming}
      disabled={!checked}
    >
      <Box
        component="label"
        htmlFor="sms-consent-checkbox"
        sx={styles.consentBox(checked)}
      >
        <Checkbox
          id="sms-consent-checkbox"
          checked={checked}
          onChange={(e) => setChecked(e.target.checked)}
          disableRipple
          icon={<Box sx={styles.box(false)} />}
          checkedIcon={
            <Box sx={styles.box(true)}>
              <TickGlyph sx={styles.tick} />
            </Box>
          }
          sx={styles.checkbox}
        />
        <Typography sx={styles.consentText}>
          {strings.smsDelivery.consentCheckbox}
        </Typography>
      </Box>

      <Box sx={styles.card}>
        {infoRow(
          ScheduleOutlinedIcon,
          strings.smsDelivery.consentFrequencyLabel,
          strings.smsDelivery.consentFrequency,
        )}
        {infoRow(
          PaymentsOutlinedIcon,
          strings.smsDelivery.consentRatesLabel,
          strings.smsDelivery.consentRates,
        )}
        {infoRow(
          HelpOutlineOutlinedIcon,
          strings.smsDelivery.consentHelpLabel,
          strings.smsDelivery.consentHelp,
        )}
      </Box>

      <Box sx={styles.links}>
        <Link
          href={urls.tNcUrl}
          target="_blank"
          rel="noopener noreferrer"
          sx={styles.link}
        >
          {strings.smsDelivery.consentTermsLabel}
        </Link>
        <Box component="span" sx={styles.linkSep}>
          •
        </Box>
        <Link
          href={urls.privacyUrl}
          target="_blank"
          rel="noopener noreferrer"
          sx={styles.link}
        >
          {strings.smsDelivery.consentPrivacyLabel}
        </Link>
      </Box>
    </ConfirmModal>
  );
};

const styles = {
  consentBox: (on: boolean) => ({
    display: "flex",
    alignItems: "flex-start",
    gap: 1.25,
    p: "14px 16px",
    borderRadius: "12px",
    border: `1.5px solid ${on ? accent : "#E7E5EF"}`,
    bgcolor: on ? "#FFF7ED" : "#FAFAFC",
    cursor: "pointer",
    transition: "border-color .15s, background-color .15s",
  }),
  checkbox: { p: 0, mt: "1px" },
  box: (on: boolean) => ({
    width: 19,
    height: 19,
    borderRadius: "5px",
    border: `1.5px solid ${on ? accent : "#B9B5C9"}`,
    bgcolor: on ? accent : "#FFFFFF",
    color: "#FFFFFF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxSizing: "border-box",
    flexShrink: 0,
  }),
  tick: { width: 12, height: 12 },
  consentText: {
    fontSize: "0.85rem",
    lineHeight: 1.5,
    fontWeight: 500,
    color: "text.primary",
  },

  card: {
    mt: 2,
    p: 2,
    borderRadius: "12px",
    bgcolor: "#F7F6FA",
    display: "flex",
    flexDirection: "column",
    gap: 1.75,
  },
  infoRow: { display: "flex", alignItems: "flex-start", gap: 1.25 },
  infoIconTile: {
    width: 30,
    height: 30,
    borderRadius: "8px",
    flexShrink: 0,
    bgcolor: "#FFF7ED",
    color: accent,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  infoIcon: { fontSize: 16 },
  infoText: {
    fontSize: "0.8rem",
    color: "text.secondary",
    lineHeight: 1.5,
    pt: "1px",
  },
  infoLabel: {
    display: "block",
    fontWeight: 700,
    color: "text.primary",
    fontSize: "0.8rem",
  },

  links: {
    mt: 2.5,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: 1.5,
    fontSize: "0.8rem",
  },
  link: {
    color: accent,
    fontWeight: 600,
    textDecoration: "none",
    "&:hover": { textDecoration: "underline" },
  },
  linkSep: { color: "text.disabled", fontSize: "0.65rem" },
} as const;

export default SmsConsentDialog;
